/**
 * Each required platform has its own price per 1,000 views.
 * Older campaigns with no platform rows keep the single campaign rate
 * on every listed platform.
 */
import type { CSSProperties } from "react";
import { formatMoney, fromMinorString, type CurrencyCode } from "@contracts/money";
import { PlatformIcon, platformMeta, visiblePlatforms } from "@app/shared/Platforms";

export interface StoredPlatformRate {
  platform: string;
  cpm_gross_minor: string;
}

export interface CreatorPlatformRate {
  id: string;
  label: string;
  cpm: string;
}

export function platformRateLabel(minor: string, currency: string): string {
  return `${formatMoney(fromMinorString(minor, currency as CurrencyCode))} / 1K`;
}

/** Lowest price per 1,000 views across the listed platforms. */
export function lowestPlatformRate(rates: readonly CreatorPlatformRate[]): string | null {
  let best: number | null = null;
  for (const row of rates) {
    const n = Number(row.cpm);
    if (!Number.isSafeInteger(n) || n <= 0) continue;
    if (best == null || n < best) best = n;
  }
  return best == null ? null : String(best);
}

export function creatorPlatformRates(opts: {
  routes: readonly string[] | null | undefined;
  rates?: readonly StoredPlatformRate[] | null | undefined;
  fallbackCpm?: string | number | null | undefined;
}): CreatorPlatformRate[] {
  const byPlatform = new Map<string, string>();
  for (const row of opts.rates ?? []) {
    const id = row.platform.trim().toLowerCase();
    const cpm = String(row.cpm_gross_minor ?? "").trim();
    if (!id || !cpm) continue;
    byPlatform.set(id, cpm);
  }
  const fallback = opts.fallbackCpm == null || opts.fallbackCpm === ""
    ? null
    : String(opts.fallbackCpm);
  const listed = visiblePlatforms(opts.routes);
  if (listed.length === 0) {
    return [...byPlatform.entries()].map(([id, cpm]) => ({
      id,
      label: platformMeta(id).label,
      cpm,
    }));
  }
  return listed.flatMap((platform) => {
    const cpm = byPlatform.get(platform.id) ?? fallback;
    if (!cpm) return [];
    return [{ id: platform.id, label: platform.label, cpm }];
  });
}

export function PlatformRateTiles({
  rows, currency,
}: {
  rows: readonly CreatorPlatformRate[];
  currency: string;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="cm-platform-rate-row">
      {rows.map((row) => (
        <article key={row.id} className="cm-platform-rate">
          <span className="cm-platform-rate-mark">
            <PlatformIcon id={row.id} size={28} title={row.label} />
          </span>
          <span className="cm-platform-rate-name">{row.label}</span>
          <strong className="cm-platform-rate-price">
            {formatMoney(fromMinorString(row.cpm, currency as CurrencyCode))}
          </strong>
          <span className="cm-platform-rate-unit">per 1,000 views</span>
        </article>
      ))}
    </div>
  );
}

export function PlatformRateList({
  rows, currency, onDark = false,
}: {
  rows: readonly CreatorPlatformRate[];
  currency: string;
  onDark?: boolean;
}) {
  if (rows.length === 0) return null;
  const line: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: onDark ? 12 : 14,
    lineHeight: 1.35,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  };
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: onDark ? 3 : 6 }}>
      {rows.map((row) => (
        <li key={row.id} style={line}>
          <PlatformIcon id={row.id} size={onDark ? 14 : 18} title={row.label} />
          <span style={{
            fontWeight: 650,
            color: onDark ? "rgba(255,255,255,.92)" : "var(--text)",
          }}>
            {row.label}
          </span>
          <span style={{
            fontFamily: "var(--font-mono)",
            color: onDark ? "var(--hero-accent)" : "var(--text)",
          }}>
            {platformRateLabel(row.cpm, currency)}
          </span>
        </li>
      ))}
    </ul>
  );
}
